import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, numberID, formatDateTime } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Boxes, PackageX, AlertTriangle, Lock, PackageCheck, Truck, ShieldCheck,
  Warehouse, Factory, Printer, ArrowRight, TrendingUp, TrendingDown, Activity, Coins, Layers, Shirt,
} from "lucide-react";

const FG_GROUP_LABELS = {
  fabric_type: "Jenis Kain",
  color: "Warna",
  size: "Ukuran",
  sleeve: "Lengan",
  age: "Kategori Usia",
};

const fmtUnit = (u) => `${numberID(u.current_stock ?? u.remaining ?? 0)} ${u.unit}`;

export default function InventoryDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [movement, setMovement] = useState(null);
  const [moving, setMoving] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [costVal, setCostVal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("today");
  const [cf, setCf] = useState("");
  const [ct, setCt] = useState("");
  const [activity, setActivity] = useState(null);
  const [cwUsage, setCwUsage] = useState(null);
  const [fgGroup, setFgGroup] = useState("fabric_type");
  const [fgCache, setFgCache] = useState({}); // group_by -> response (local page cache, no backend cache)

  useEffect(() => {
    setLoading(true);
    // Core dashboard (SNAPSHOT, period-independent). Renders as soon as this resolves.
    api.get("/inventory/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // Heavy analytics load progressively; a failure in any one must NOT block the core dashboard.
    api.get("/inventory/dashboard/movement").then((r) => setMovement(r.data)).catch(() => setMovement({ error: true }));
    api.get("/inventory/dashboard/moving").then((r) => setMoving(r.data)).catch(() => setMoving({ error: true }));
    api.get("/inventory/dashboard/valuation").then((r) => setValuation(r.data)).catch(() => setValuation({ error: true }));
    api.get("/inventory/cost-valuation").then((r) => setCostVal(r.data)).catch(() => {});
  }, []);

  // Period-driven ACTIVITY sections (do NOT affect snapshot KPI above).
  useEffect(() => {
    if (period === "custom" && (!cf || !ct)) return;
    const params = period === "custom" ? { period, start_date: cf, end_date: ct } : { period };
    setActivity(null); setCwUsage(null);
    api.get("/inventory/dashboard/activity", { params }).then((r) => setActivity(r.data)).catch(() => setActivity({ error: true }));
    api.get("/inventory/dashboard/catch-weight-usage", { params }).then((r) => setCwUsage(r.data)).catch(() => setCwUsage({ error: true }));
  }, [period, cf, ct]);

  // Finished Goods snapshot (period-independent). Cache per group_by locally so toggling back is instant.
  useEffect(() => {
    if (fgCache[fgGroup]) return;
    api.get("/inventory/dashboard/finished-goods", { params: { group_by: fgGroup } })
      .then((r) => setFgCache((c) => ({ ...c, [fgGroup]: r.data })))
      .catch(() => setFgCache((c) => ({ ...c, [fgGroup]: { error: true } })));
  }, [fgGroup, fgCache]);

  if (loading) return <Loading label="Memuat Dashboard Inventory..." />;
  if (!data) return <EmptyState title="Data tidak tersedia" />;

  const { kpi, pusat, konveksi, printing, alerts } = data;
  const fg = fgCache[fgGroup];

  return (
    <div data-testid="inventory-dashboard">
      <PageHeader
        title="Dashboard Inventory"
        subtitle="Pusat monitoring READ-ONLY di atas sistem inventory existing. Available = On Hand − Reserved."
        testId="inv-dash-header"
      />

      {/* TOP KPI */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="inv-dash-kpi">
        <StatCard label="Active Item / SKU" value={numberID(kpi.active_items_total)} icon={Boxes} accent="indigo"
          hint={`${numberID(kpi.active_sku)} SKU · ${numberID(kpi.active_material)} material`} testId="kpi-active-items" />
        <StatCard label="Stockout" value={numberID(kpi.stockout)} icon={PackageX} accent="rose" testId="kpi-stockout" />
        <StatCard label="Low Stock" value={numberID(kpi.low_stock)} icon={AlertTriangle} accent="amber" testId="kpi-lowstock" />
        <StatCard label="Reserved Stock" value={`${numberID(kpi.reserved_stock)} pcs`} icon={Lock} accent="sky" testId="kpi-reserved" />
        <StatCard label="Available Stock" value={`${numberID(kpi.available_stock)} pcs`} icon={PackageCheck} accent="emerald" testId="kpi-available" />
        <StatCard label="Incoming Stock" value={`${numberID(kpi.incoming_po_count)} PO`} icon={Truck} accent="sky"
          hint={kpi.incoming_by_unit.length ? kpi.incoming_by_unit.map((u) => `${numberID(u.remaining)} ${u.unit}`).join(" · ") : "Tidak ada PO aktif"} testId="kpi-incoming" />
        <StatCard label="FG Menunggu Transfer" value={`${numberID(kpi.finished_goods_waiting_transfer)} pcs`} icon={Factory} accent="amber" testId="kpi-fg" />
        <StatCard label="Inventory Integrity" value={numberID(kpi.inventory_integrity_anomalies)} icon={ShieldCheck}
          accent={kpi.inventory_integrity_anomalies > 0 ? "rose" : "emerald"} hint="Jumlah anomali" testId="kpi-integrity" />
      </div>
      <p className="mt-2 text-xs text-slate-400">{kpi.unit_note}</p>

      {/* INVENTORY SUMMARY — 3 cards */}
      <h2 className="mt-8 mb-3 font-display text-lg font-semibold text-slate-800">Ringkasan Inventory</h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="inv-dash-summary">
        <SummaryCard
          title="Inventory Pusat" icon={Warehouse} accent="indigo" testId="summary-pusat"
          onDrill={() => navigate("/inventory/pusat")}
          rows={[
            ["Active SKU", numberID(pusat.active_sku)],
            ["On Hand", `${numberID(pusat.on_hand)} pcs`],
            ["Reserved", `${numberID(pusat.reserved)} pcs`],
            ["Available", `${numberID(pusat.available)} pcs`],
            ["Low Stock", numberID(pusat.low_stock)],
            ["Stockout", numberID(pusat.stockout)],
            ["Stock Movement", numberID(pusat.movement_count)],
          ]}
        />
        <SummaryCard
          title="Inventory Konveksi" icon={Factory} accent="amber" testId="summary-konveksi"
          onDrill={() => navigate("/inventory/konveksi")}
          rows={[
            ["Active Material", numberID(konveksi.active_material)],
            ["Stockout", numberID(konveksi.stockout)],
            ["Low Stock", numberID(konveksi.low_stock)],
            ["Current Stock", konveksi.current_stock_by_unit.length ? konveksi.current_stock_by_unit.map(fmtUnit).join(" · ") : "-"],
            ["Finished Goods", `${numberID(konveksi.finished_goods.total)} pcs`],
            ["Material Movement", numberID(konveksi.movement_count)],
            ["Incoming Purchasing", `${numberID(konveksi.incoming.po_count)} PO`],
          ]}
        />
        <SummaryCard
          title="Inventory Digital Printing" icon={Printer} accent="sky" testId="summary-printing"
          onDrill={() => navigate("/inventory/printing")}
          rows={[
            ["Active Material", numberID(printing.active_material)],
            ["Current Stock", printing.current_stock_by_unit.length ? printing.current_stock_by_unit.map(fmtUnit).join(" · ") : "-"],
            ["Stockout", numberID(printing.stockout)],
            ["Low Stock", numberID(printing.low_stock)],
            ["Usage / Movement", numberID(printing.movement_count)],
            ["Incoming Purchasing", `${numberID(printing.incoming.po_count)} PO`],
          ]}
        />
      </div>

      {/* CRITICAL ALERTS */}
      <h2 className="mt-8 mb-3 font-display text-lg font-semibold text-slate-800">Critical Alerts</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="inv-dash-alerts">
        <AlertPill label="Stockout" count={alerts.stockout} onClick={() => navigate("/inventory/pusat")} testId="alert-stockout" />
        <AlertPill label="Low Stock" count={alerts.low_stock} onClick={() => navigate("/inventory/pusat")} testId="alert-lowstock" />
        <AlertPill label="On Hand < 0" count={alerts.negative_on_hand.count} testId="alert-neg-onhand" />
        <AlertPill label="Reserved < 0" count={alerts.negative_reserved.count} testId="alert-neg-reserved" />
        <AlertPill label="Reserved > On Hand" count={alerts.reserved_gt_on_hand.count} testId="alert-reserved-gt" />
        <AlertPill label="Integrity Anomali" count={kpi.inventory_integrity_anomalies} testId="alert-integrity" />
      </div>
      <p className="mt-2 text-xs text-slate-400">{alerts.note}</p>

      {/* ===== STOK BARANG JADI SAAT INI (SNAPSHOT — period-independent) ===== */}
      <div className="mt-8" data-testid="inv-fg-section">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shirt className="h-5 w-5 text-indigo-600" />
            <h2 className="font-display text-lg font-semibold text-slate-800">Stok Barang Jadi Saat Ini</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Kelompokkan per</span>
            <Select value={fgGroup} onValueChange={setFgGroup}>
              <SelectTrigger className="w-40" data-testid="fg-group-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FG_GROUP_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k} data-testid={`fg-group-${k}`}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="mb-3 text-xs italic text-slate-500">Snapshot · tidak terpengaruh filter periode. Siap jual = Pusat (Available = On Hand − Reserved). Stok Konveksi menunggu transfer ditampilkan terpisah dan tidak dijumlahkan.</p>

        {!fg ? <SectionSkeleton testId="fg-skeleton" /> : fg.error ? <SectionError testId="fg-error" /> : (
          <>
            {/* KPI ready-to-sell (PUSAT) vs waiting transfer (KONVEKSI) */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="fg-kpi">
              <StatCard label="Siap Jual (Pusat) — Available" value={`${numberID(fg.ready_to_sell.available)} pcs`} icon={PackageCheck} accent="emerald" testId="fg-available" />
              <StatCard label="On Hand (Pusat)" value={`${numberID(fg.ready_to_sell.on_hand)} pcs`} icon={Warehouse} accent="indigo" hint={`${numberID(fg.ready_to_sell.sku_count)} SKU aktif`} testId="fg-onhand" />
              <StatCard label="Reserved (Pusat)" value={`${numberID(fg.ready_to_sell.reserved)} pcs`} icon={Lock} accent="sky" testId="fg-reserved" />
              <StatCard label="Menunggu Transfer (Konveksi)" value={`${numberID(fg.waiting_transfer_konveksi.total)} pcs`} icon={Factory} accent="amber" testId="fg-waiting-total" />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Grouping breakdown (ready-to-sell PUSAT) */}
              <Panel title={`Siap Jual per ${FG_GROUP_LABELS[fgGroup]} (Pusat)`} icon={Shirt} testId="panel-fg-groups">
                {fg.ready_to_sell.groups.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-slate-400">Belum ada stok barang jadi di Pusat</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>{FG_GROUP_LABELS[fgGroup]}</TableHead>
                      <TableHead className="text-right">On Hand</TableHead>
                      <TableHead className="text-right">Reserved</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="text-right">SKU</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {fg.ready_to_sell.groups.map((g) => (
                        <TableRow key={g.key} data-testid={`fg-group-row-${g.key}`}>
                          <TableCell className="text-xs font-medium text-slate-800">{g.label}</TableCell>
                          <TableCell className="text-right">{numberID(g.on_hand)}</TableCell>
                          <TableCell className="text-right">{numberID(g.reserved)}</TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600">{numberID(g.available)}</TableCell>
                          <TableCell className="text-right text-slate-500">{numberID(g.sku_count)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Panel>

              {/* Waiting transfer KONVEKSI — always per fabric type */}
              <Panel title="Menunggu Transfer (Konveksi) per Jenis Kain" icon={Factory} testId="panel-fg-waiting">
                <div className="px-4 py-3 text-xs text-amber-700">{fg.waiting_transfer_konveksi.note}</div>
                {fg.waiting_transfer_konveksi.breakdown.length === 0 ? (
                  <p className="px-4 pb-8 text-center text-sm text-slate-400">Tidak ada stok Konveksi menunggu transfer</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Jenis Kain</TableHead>
                      <TableHead className="text-right">Jumlah (pcs)</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {fg.waiting_transfer_konveksi.breakdown.map((b) => (
                        <TableRow key={b.key} data-testid={`fg-waiting-row-${b.key}`}>
                          <TableCell className="text-xs font-medium text-slate-800">{b.fabric_type}</TableCell>
                          <TableCell className="text-right font-semibold text-amber-600">{numberID(b.pcs)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Panel>
            </div>

            {/* ATTENTION TABLE (SKU-level, semantics tidak berubah) */}
            <div className="mt-6">
              <Panel title="Perhatian Stok Barang Jadi" icon={AlertTriangle} testId="panel-fg-attention"
                action={{ label: "Inventory Pusat", onClick: () => navigate("/inventory/pusat") }}>
                <p className="px-4 py-3 text-xs text-slate-500">{fg.attention.note}</p>
                {fg.attention.stockout.length === 0 && fg.attention.low_stock.length === 0 ? (
                  <p className="px-4 pb-8 text-center text-sm text-slate-400">Tidak ada SKU stockout / low stock di Pusat</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">On Hand</TableHead>
                      <TableHead className="text-right">Reserved</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="text-right">Min</TableHead>
                      <TableHead className="text-right">Di Konveksi</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {[...fg.attention.stockout, ...fg.attention.low_stock].map((r) => (
                        <TableRow key={r.sku} data-testid={`fg-attention-${r.sku}`}>
                          <TableCell className="font-mono text-xs">{r.sku}</TableCell>
                          <TableCell><StatusBadge status={r.level === "OUT" ? "Habis" : "Rendah"} label={r.level === "OUT" ? "Stockout" : "Low Stock"} /></TableCell>
                          <TableCell className="text-right">{numberID(r.on_hand)}</TableCell>
                          <TableCell className="text-right">{numberID(r.reserved)}</TableCell>
                          <TableCell className={`text-right font-semibold ${r.available <= 0 ? "text-rose-600" : "text-amber-600"}`}>{numberID(r.available)}</TableCell>
                          <TableCell className="text-right text-slate-400">{numberID(r.minimum_stock)}</TableCell>
                          <TableCell className="text-right">{r.waiting_transfer_konveksi > 0 ? <span className="font-medium text-amber-600" data-testid={`fg-attention-wtk-${r.sku}`}>{numberID(r.waiting_transfer_konveksi)} pcs</span> : <span className="text-slate-300">—</span>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Panel>
            </div>
          </>
        )}
      </div>

      {/* ===== PERIOD-DRIVEN ACTIVITY + CATCH WEIGHT USAGE (snapshot KPI di atas tidak terpengaruh) ===== */}
      <div className="mt-8" data-testid="inv-period-section">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-base font-semibold text-slate-700">Aktivitas &amp; Catch Weight Usage · Periode</h3>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-44" data-testid="inv-period-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Hari Ini</SelectItem>
                <SelectItem value="7d">7 Hari Terakhir</SelectItem>
                <SelectItem value="month">Bulan Ini</SelectItem>
                <SelectItem value="ytd">Tahun Ini</SelectItem>
                <SelectItem value="custom">Pilih Tanggal</SelectItem>
              </SelectContent>
            </Select>
            {period === "custom" && (
              <>
                <input type="date" value={cf} onChange={(e) => setCf(e.target.value)} data-testid="inv-period-from" className="rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
                <span className="text-slate-400">→</span>
                <input type="date" value={ct} onChange={(e) => setCt(e.target.value)} data-testid="inv-period-to" className="rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
              </>
            )}
          </div>
        </div>
        <p className="mb-3 text-xs text-slate-400">KPI stok current di atas TIDAK terpengaruh filter periode. Section ini khusus aktivitas/movement{activity?.period ? ` · ${activity.period.from} → ${activity.period.to} WIB` : ""}.</p>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Panel title="Aktivitas Stok (Periode)" icon={Activity} testId="panel-activity">
            {!activity ? <SectionSkeleton testId="activity-skeleton" /> : activity.error ? <SectionError testId="activity-error" /> : (
              <div className="space-y-3 px-4 py-3">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(activity.movement).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600" data-testid={`activity-bucket-${k}`}>{k}<span className="font-bold text-slate-900">{numberID(v)}</span></span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="activity-receiving"><p className="text-[11px] text-slate-400">Receiving (GR)</p><p className="font-display text-base font-bold text-slate-900">{numberID(activity.receiving.gr_count)}</p></div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="activity-fg"><p className="text-[11px] text-slate-400">Finished Goods (pcs)</p><p className="font-display text-base font-bold text-slate-900">{numberID(activity.finished_goods.pcs)}</p></div>
                </div>
              </div>
            )}
          </Panel>

          <Panel title="Catch Weight Usage (Periode)" icon={Layers} testId="panel-cw-usage">
            {!cwUsage ? <SectionSkeleton testId="cwusage-skeleton" /> : cwUsage.error ? <SectionError testId="cwusage-error" /> : (
              <div className="space-y-3 px-4 py-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[["Roll Terpakai dalam Periode", numberID(cwUsage.totals.rolls_used_distinct), "cw-rolls-used"],
                    ["Usage KG dalam Periode", numberID(cwUsage.totals.usage_kg), "cw-usage-kg"],
                    ["Waste KG", numberID(cwUsage.totals.waste_kg), "cw-waste-kg"],
                    ["Return KG", numberID(cwUsage.totals.return_kg), "cw-return-kg"],
                    ["Konsumsi Bersih (Usage + Waste)", numberID(cwUsage.totals.net_consumption_kg), "cw-net-kg"],
                    ["Avg Usage / Roll", numberID(cwUsage.totals.avg_usage_kg_per_roll), "cw-avg-roll"]].map(([l, v, tid]) => (
                    <div key={tid} className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2" data-testid={tid}>
                      <p className="text-[11px] text-slate-500">{l}</p>
                      <p className="font-display text-base font-bold text-slate-900">{v}</p>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="cw-issued-pending">
                  <b>Issued Belum Reconcile</b> (backlog saat ini): {numberID(cwUsage.issued_pending.rolls)} roll · {numberID(cwUsage.issued_pending.issued_kg)} KG — tidak dihitung sebagai Usage/Waste/Return/Net.
                </div>
                {cwUsage.breakdown.length === 0 ? (
                  <p className="py-4 text-center text-sm text-slate-400">Belum ada usage Catch Weight pada periode ini</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Item / Jenis / Warna</TableHead><TableHead className="text-right">Roll</TableHead>
                      <TableHead className="text-right">Usage</TableHead><TableHead className="text-right">Waste</TableHead>
                      <TableHead className="text-right">Return</TableHead><TableHead className="text-right">Net</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {cwUsage.breakdown.map((b, i) => (
                        <TableRow key={b.inventory_item_id || i} data-testid={`cw-breakdown-${i}`}>
                          <TableCell className="text-xs"><p className="font-medium text-slate-800">{b.item_name}</p><p className="text-[11px] text-slate-400">{[b.fabric_type, b.color].filter(Boolean).join(" · ") || "—"}</p></TableCell>
                          <TableCell className="text-right">{numberID(b.rolls_used_distinct)}</TableCell>
                          <TableCell className="text-right">{numberID(b.usage_kg)}</TableCell>
                          <TableCell className="text-right">{numberID(b.waste_kg)}</TableCell>
                          <TableCell className="text-right">{numberID(b.return_kg)}</TableCell>
                          <TableCell className="text-right font-semibold">{numberID(b.net_consumption_kg)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Panel>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* STOCK MOVEMENT (lazy) */}
        <Panel title="Stock Movement" icon={Activity} testId="panel-movement"
          action={{ label: "Lihat Riwayat", onClick: () => navigate("/inventory/pusat") }}>
          {!movement ? <SectionSkeleton testId="movement-skeleton" /> : movement.error ? <SectionError testId="movement-error" /> : (
          <>
          <div className="flex flex-wrap gap-2 px-4 py-3">
            {Object.entries(movement.buckets).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600" data-testid={`move-bucket-${k}`}>
                {k}<span className="font-bold text-slate-900">{numberID(v)}</span>
              </span>
            ))}
          </div>
          {movement.recent.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Belum ada pergerakan stok</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tipe</TableHead><TableHead>Item</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead>Waktu</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {movement.recent.map((m, i) => (
                  <TableRow key={i} data-testid={`move-recent-${i}`}>
                    <TableCell><StatusBadge status={m.bucket} label={m.type} /></TableCell>
                    <TableCell className="font-mono text-xs">{m.label}</TableCell>
                    <TableCell className={`text-right font-medium ${m.qty < 0 ? "text-rose-600" : "text-emerald-600"}`}>{numberID(m.qty)} {m.unit}</TableCell>
                    <TableCell className="text-xs text-slate-400">{formatDateTime(m.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          </>
          )}
        </Panel>

        {/* INCOMING PURCHASING */}
        <Panel title="Incoming Purchasing" icon={Truck} testId="panel-incoming"
          action={{ label: "Purchase Order", onClick: () => navigate("/purchasing/orders") }}>
          {data.incoming_all.orders.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Tidak ada PO aktif dengan sisa penerimaan</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>PO</TableHead><TableHead className="text-right">Ordered</TableHead>
                <TableHead className="text-right">Received</TableHead><TableHead className="text-right">Remaining</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.incoming_all.orders.map((o) => (
                  <TableRow key={o.po_no} data-testid={`incoming-${o.po_no}`}>
                    <TableCell><p className="font-mono text-xs font-semibold">{o.po_no}</p><p className="text-[11px] text-slate-400">{o.supplier_name}</p></TableCell>
                    <TableCell className="text-right">{numberID(o.ordered)}</TableCell>
                    <TableCell className="text-right">{numberID(o.received)}</TableCell>
                    <TableCell className="text-right font-semibold text-amber-600">{numberID(o.remaining)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>

        {/* FINISHED GOODS WAITING TRANSFER */}
        <Panel title="Finished Goods Menunggu Transfer" icon={Factory} testId="panel-fg"
          action={{ label: "Transfer Stok", onClick: () => navigate("/inventory/transfer") }}>
          {konveksi.finished_goods.list.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Tidak ada barang jadi menunggu transfer</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>SKU</TableHead><TableHead className="text-right">On Hand</TableHead>
                <TableHead className="text-right">Reserved</TableHead><TableHead className="text-right">Available</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {konveksi.finished_goods.list.map((f) => (
                  <TableRow key={f.sku} data-testid={`fg-${f.sku}`}>
                    <TableCell className="font-mono text-xs">{f.sku}</TableCell>
                    <TableCell className="text-right">{numberID(f.on_hand)}</TableCell>
                    <TableCell className="text-right">{numberID(f.reserved)}</TableCell>
                    <TableCell className="text-right font-semibold">{numberID(f.available)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>

        {/* FAST / SLOW MOVING (lazy) */}
        <Panel title="Fast / Slow Moving" icon={TrendingUp} testId="panel-moving">
          {!moving ? <SectionSkeleton testId="moving-skeleton" /> : moving.error ? <SectionError testId="moving-error" /> : (
          <div className="px-4 py-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-600"><TrendingUp className="h-4 w-4" /> Fast Moving (Outbound 30 hari)</p>
            {moving.fast.length === 0 ? <p className="py-3 text-sm text-slate-400">Belum ada data</p> : (
              <div className="space-y-1">
                {moving.fast.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-sm" data-testid={`fast-${i}`}>
                    <span className="font-mono text-xs text-slate-700">{f.label}</span>
                    <span className="text-slate-500">7h: <b>{numberID(f.out_7d)}</b> · 30h: <b>{numberID(f.out_30d)}</b> {f.unit}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="mb-2 mt-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-rose-600"><TrendingDown className="h-4 w-4" /> Slow Moving (Lama tak bergerak)</p>
            {moving.slow.length === 0 ? <p className="py-3 text-sm text-slate-400">Belum ada data</p> : (
              <div className="space-y-1">
                {moving.slow.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm" data-testid={`slow-${i}`}>
                    <span className="font-mono text-xs text-slate-700">{s.label}</span>
                    <span className="text-slate-500">{s.days_since_movement == null ? "Belum pernah bergerak" : `${numberID(s.days_since_movement)} hari`} · {numberID(s.on_hand)} {s.unit}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </Panel>
      </div>

      {/* INVENTORY VALUE (lazy) */}
      <h2 className="mt-8 mb-3 font-display text-lg font-semibold text-slate-800">Estimasi Nilai Inventory</h2>
      {!valuation ? <SectionSkeleton testId="valuation-skeleton" /> : valuation.error ? <SectionError testId="valuation-error" /> : (
      <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="inv-dash-valuation">
        <StatCard label="Estimasi Nilai (HPP Disetujui)" value={rupiah(valuation.total_value)} icon={Coins} accent="emerald" testId="val-total" />
        <StatCard label="SKU Ber-HPP Approved" value={numberID(valuation.priced_sku_count)} icon={PackageCheck} accent="indigo" testId="val-priced" />
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="val-missing">
          <div className="flex items-center gap-2"><AlertTriangle className={`h-4 w-4 ${valuation.missing_hpp_count > 0 ? "text-amber-600" : "text-slate-400"}`} /><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">SKU Tanpa HPP Approved</p></div>
          <p className="mt-2 font-display text-xl font-bold text-slate-900">{numberID(valuation.missing_hpp_count)}</p>
          {valuation.missing_hpp_count > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="link" size="sm" className="mt-1 h-auto p-0 text-xs text-indigo-600" data-testid="val-missing-view">Lihat Daftar</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="val-missing-dialog">
                <DialogHeader><DialogTitle>SKU Tanpa HPP Approved ({numberID(valuation.missing_hpp_count)})</DialogTitle></DialogHeader>
                <p className="text-xs text-slate-500">{valuation.missing_label}. Daftar ini read-only; tidak mengubah data HPP.</p>
                <div className="mt-2 max-h-80 overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5">
                    {valuation.missing_hpp_skus.map((s) => (
                      <span key={s} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-700" data-testid={`val-missing-sku-${s}`}>{s}</span>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      </>
      )}

      {/* ESTIMASI NILAI PERSEDIAAN SPH (cost-based, multi-inventory) */}
      {costVal && (
        <div data-testid="inv-dash-cost-valuation">
          <h2 className="mt-8 mb-1 font-display text-lg font-semibold text-slate-800">Estimasi Nilai Persediaan SPH</h2>
          <p className="mb-3 text-xs italic text-slate-500">{costVal.label}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <CostValCard title="Inventory Pusat" v={costVal.pusat} icon={Warehouse} accent="indigo" testId="costval-pusat" />
            <CostValCard title="Inventory Konveksi" v={costVal.konveksi} icon={Factory} accent="amber" testId="costval-konveksi" />
            <CostValCard title="Inventory Digital Printing" v={costVal.printing} icon={Printer} accent="sky" testId="costval-printing" />
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm" data-testid="costval-total">
              <div className="flex items-center gap-2"><Coins className="h-4 w-4 text-emerald-600" /><p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Total Estimasi Nilai SPH</p></div>
              <p className="mt-2 font-display text-2xl font-bold text-emerald-700">{rupiah(costVal.total_estimated)}</p>
              <p className="mt-1 text-xs text-emerald-700">Coverage {costVal.overall_coverage_pct}% · {costVal.total_priced}/{costVal.total_items} item</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500" data-testid="costval-missing">
            <b>{numberID(costVal.items_without_reliable_cost)}</b> item belum memiliki reliable cost (tidak dihitung sebagai Rp0). Angka di atas adalah estimasi — <b>bukan Nilai Persediaan Accounting resmi</b>.
          </p>
        </div>
      )}
    </div>
  );
}

function SectionSkeleton({ testId }) {
  return (
    <div className="animate-pulse space-y-2 px-4 py-4" data-testid={testId}>
      <div className="h-3 w-1/3 rounded bg-slate-100" />
      <div className="h-3 w-2/3 rounded bg-slate-100" />
      <div className="h-3 w-1/2 rounded bg-slate-100" />
      <div className="h-3 w-3/5 rounded bg-slate-100" />
    </div>
  );
}

function SectionError({ testId }) {
  return (
    <p className="px-4 py-8 text-center text-sm text-slate-400" data-testid={testId}>
      Bagian ini gagal dimuat. Data utama tetap tersedia — muat ulang halaman untuk mencoba lagi.
    </p>
  );
}

function CostValCard({ title, v, icon: Icon, accent, testId }) {
  const accents = { indigo: "text-indigo-600", amber: "text-amber-600", sky: "text-sky-600" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-2"><Icon className={`h-4 w-4 ${accents[accent]}`} /><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p></div>
      <p className="mt-2 font-display text-xl font-bold text-slate-900">{rupiah(v.estimated_value)}</p>
      <p className="mt-1 text-xs text-slate-500">Coverage {v.coverage_pct}% · {v.coverage}</p>
      {v.not_priced > 0 && <p className="mt-0.5 text-[11px] text-amber-600">{v.not_priced} item belum dinilai</p>}
    </div>
  );
}

function SummaryCard({ title, icon: Icon, accent, rows, onDrill, testId }) {
  const accents = {
    indigo: "bg-indigo-50 text-indigo-600", amber: "bg-amber-50 text-amber-600", sky: "bg-sky-50 text-sky-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accents[accent]}`}><Icon className="h-4.5 w-4.5" /></div>
          <p className="font-display text-sm font-semibold text-slate-800">{title}</p>
        </div>
      </div>
      <div className="divide-y divide-slate-50">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-4 py-2">
            <span className="text-xs text-slate-500">{k}</span>
            <span className="text-sm font-semibold text-slate-800">{v}</span>
          </div>
        ))}
      </div>
      <button onClick={onDrill} data-testid={`${testId}-drill`}
        className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 px-4 py-2.5 text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-50">
        Buka Module <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AlertPill({ label, count, onClick, testId }) {
  const active = count > 0;
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testId}
      className={`rounded-xl border p-4 text-left transition-shadow ${active ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"} ${onClick ? "hover:shadow-md" : "cursor-default"}`}>
      <p className={`font-display text-2xl font-bold ${active ? "text-rose-600" : "text-slate-400"}`}>{numberID(count)}</p>
      <p className="mt-1 text-xs font-medium text-slate-500">{label}</p>
    </button>
  );
}

function Panel({ title, icon: Icon, children, action, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          <p className="font-display text-sm font-semibold text-slate-800">{title}</p>
        </div>
        {action && (
          <button onClick={action.onClick} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline" data-testid={`${testId}-action`}>
            {action.label} <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
